import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { RinkDiagram } from "../components/RinkDiagram";
import {
  AGE_GROUPS,
  DRILL_CATEGORIES,
  DRILL_FOCUS_AREAS,
  type AgeGroup,
  type Drill,
  type DrillCategory,
  type DrillFocusArea,
} from "../domain/drill";
import { deleteCloudDrill } from "../lib/cloud-drill-storage";
import { deleteDrill, loadDrills } from "../lib/drill-storage";

export function LibraryRoute() {
  const navigate = useNavigate();
  const [drills, setDrills] = useState<Drill[]>(() => loadDrills());
  const [query, setQuery] = useState("");
  const [folder, setFolder] = useState("all");
  const [age, setAge] = useState<"all" | AgeGroup>("all");
  const [category, setCategory] = useState<"all" | DrillCategory>("all");
  const [focus, setFocus] = useState<"all" | DrillFocusArea>("all");
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const folders = useMemo(
    () => [...new Set(drills.map((drill) => drill.folder).filter(Boolean))].sort(),
    [drills],
  );
  const visible = useMemo(
    () =>
      drills.filter((drill) => {
        const matchesQuery =
          !query ||
          `${drill.title} ${drill.objective} ${drill.folder} ${drill.category} ${drill.focusAreas.join(" ")} ${drill.ageGroups.join(" ")}`
            .toLowerCase()
            .includes(query.toLowerCase());
        const matchesFolder =
          folder === "all" ||
          (folder === "unfiled" ? !drill.folder : drill.folder === folder);
        return (
          matchesQuery &&
          matchesFolder &&
          (age === "all" || drill.ageGroups.includes(age)) &&
          (category === "all" || drill.category === category) &&
          (focus === "all" || drill.focusAreas.includes(focus))
        );
      }),
    [age, category, drills, focus, folder, query],
  );

  const clearFilters = () => {
    setQuery("");
    setFolder("all");
    setAge("all");
    setCategory("all");
    setFocus("all");
  };

  const remove = (drill: Drill) => {
    if (window.confirm(`Delete “${drill.title}” from your drill library?`)) {
      setDrills(deleteDrill(drill.id));
      void deleteCloudDrill(drill.id);
    }
  };

  const createFolder = () => {
    const name = newFolderName.trim();
    if (!name) return;
    setShowNewFolder(false);
    setNewFolderName("");
    navigate(`/drills/new?folder=${encodeURIComponent(name)}`);
  };

  return (
    <main className="library-page">
      <section className="library-hero">
        <div>
          <p className="eyebrow">Your coaching workspace</p>
          <h1>
            Build drills.
            <br />
            <span>Own your practice.</span>
          </h1>
          <p>
            Draw clear hockey drills, capture the coaching detail, and build a
            library ready for faster practice planning.
          </p>
        </div>
        <div className="library-hero-actions">
          <Link className="secondary-button large" to="/practice">
            Plan a practice
          </Link>
          <Link className="primary-button large" to="/drills/new">
            + Create a drill
          </Link>
          <button
            className="secondary-button large"
            onClick={() => setShowNewFolder(true)}
          >
            + New folder
          </button>
        </div>
      </section>

      <section className="library-content">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Drill library</p>
            <h2>
              {drills.length
                ? `${drills.length} saved ${drills.length === 1 ? "drill" : "drills"}`
                : "Your first drill starts here"}
            </h2>
          </div>
        </div>

        {drills.length > 0 && (
          <div className="library-organizer">
            <nav className="folder-tabs" aria-label="Drill folders">
              <button className={folder === "all" ? "selected" : ""} onClick={() => setFolder("all")}>
                All drills <span>{drills.length}</span>
              </button>
              {folders.map((item) => (
                <button key={item} className={folder === item ? "selected" : ""} onClick={() => setFolder(item)}>
                  {item}<span>{drills.filter((drill) => drill.folder === item).length}</span>
                </button>
              ))}
              <button className={folder === "unfiled" ? "selected" : ""} onClick={() => setFolder("unfiled")}>
                Unfiled <span>{drills.filter((drill) => !drill.folder).length}</span>
              </button>
              <button className="new-folder-tab" onClick={() => setShowNewFolder(true)}>
                ＋ New folder
              </button>
            </nav>
            <div className="library-filters">
              <label className="search-field">
                <span>Search</span>
                <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Name, objective, folder or focus…" />
              </label>
              <label>Age<select value={age} onChange={(event) => setAge(event.target.value as "all" | AgeGroup)}><option value="all">All ages</option>{AGE_GROUPS.map((item) => <option value={item} key={item}>{item}</option>)}</select></label>
              <label>Category<select value={category} onChange={(event) => setCategory(event.target.value as "all" | DrillCategory)}><option value="all">All categories</option>{DRILL_CATEGORIES.map((item) => <option value={item} key={item}>{item}</option>)}</select></label>
              <label>Focus<select value={focus} onChange={(event) => setFocus(event.target.value as "all" | DrillFocusArea)}><option value="all">All focus areas</option>{DRILL_FOCUS_AREAS.map((item) => <option value={item} key={item}>{item}</option>)}</select></label>
              <button className="text-button" onClick={clearFilters}>Clear</button>
            </div>
          </div>
        )}

        {!drills.length ? (
          <div className="empty-library">
            <div className="empty-rink">
              <RinkDiagram template="half" elements={[]} />
            </div>
            <p className="eyebrow">Nothing saved yet</p>
            <h3>Turn the drills in your head into a reusable library.</h3>
            <p>
              Choose a rink, place players and equipment, draw movement paths,
              then add the instructions you need on the ice.
            </p>
            <Link className="primary-button" to="/drills/new">
              Draw your first drill
            </Link>
          </div>
        ) : visible.length ? (
          <div className="drill-grid">
            {visible.map((drill) => (
              <article className="drill-card" key={drill.id}>
                <Link className="drill-preview" to={`/drills/${drill.id}/edit`}>
                  <RinkDiagram
                    template={drill.rink}
                    rinkSide={drill.rinkSide}
                    elements={drill.elements}
                  />
                </Link>
                <div className="drill-card-body">
                  <div className="card-meta">
                    <span>{drill.durationMinutes} min</span>
                    <span>{drill.ageGroups.join(" · ")}</span>
                    <span>{drill.category}</span>
                    {drill.folder && <span>📁 {drill.folder}</span>}
                  </div>
                  <h3>
                    <Link to={`/drills/${drill.id}/edit`}>{drill.title}</Link>
                  </h3>
                  <p>
                    {drill.objective ||
                      "Add an objective to make this drill easier to find later."}
                  </p>
                  <div className="card-actions">
                    <Link to={`/drills/${drill.id}/edit`}>Edit drill →</Link>
                    <button onClick={() => remove(drill)}>Delete</button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="no-results">
            <h3>No drills match “{query}”</h3>
            <button className="text-button" onClick={clearFilters}>
              Clear search
            </button>
          </div>
        )}
      </section>
      {showNewFolder && (
        <div className="cloud-modal-backdrop" role="presentation">
          <form
            className="cloud-modal new-folder-modal"
            onSubmit={(event) => {
              event.preventDefault();
              createFolder();
            }}
          >
            <button
              type="button"
              className="cloud-modal-close"
              onClick={() => setShowNewFolder(false)}
              aria-label="Close new folder"
            >
              ×
            </button>
            <p className="eyebrow">Drill library</p>
            <h2>Create a folder</h2>
            <p>The first drill you create will be saved inside this folder.</p>
            <label>
              Folder name
              <input
                autoFocus
                value={newFolderName}
                onChange={(event) => setNewFolderName(event.target.value)}
                placeholder="e.g. U6 season or Warm-ups"
              />
            </label>
            <button className="primary-button" type="submit" disabled={!newFolderName.trim()}>
              Create folder and drill
            </button>
          </form>
        </div>
      )}
    </main>
  );
}
